'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { KagemaneTemplate } from '@/lib/kagemane-catalog';
import { kagemaneAspectRatio, kagemaneCoverProps } from '@/lib/kagemane-media-layout';
import { useViewportPreviewVideo } from './useViewportPreviewVideo';

export function KagemaneReferenceCard({
  item, name, selected, disabled, playbackEnabled, onSelect,
}: {
  item: KagemaneTemplate;
  name: string;
  selected: boolean;
  disabled: boolean;
  playbackEnabled: boolean;
  onSelect: () => void;
}) {
  const t = useTranslations('kagemane');
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const coverProps = kagemaneCoverProps(item);
  const { containerRef, videoRef, shouldLoadVideo } = useViewportPreviewVideo(
    item.previewSrc, playbackEnabled && !videoFailed,
  );

  useEffect(() => {
    if (!shouldLoadVideo) setVideoReady(false);
  }, [shouldLoadVideo]);

  return <article ref={containerRef} role="listitem" className="min-w-0" data-kagemane-reference-card
    data-kagemane-column-span={kagemaneAspectRatio(item.aspectRatio) > 1 ? 2 : 1}>
    <button type="button" disabled={disabled} onClick={onSelect}
      aria-haspopup="dialog" aria-label={t('studioPreviewReferenceAria', { name })}
      style={{ aspectRatio: kagemaneAspectRatio(item.aspectRatio) }}
      className="group relative block w-full overflow-hidden rounded-[16px] text-left focus-visible:outline-none">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img {...coverProps} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full rounded-[16px] object-cover" />
      {shouldLoadVideo && <video ref={videoRef} src={item.previewSrc} muted loop playsInline
        poster={coverProps.src} preload="none" disablePictureInPicture
        onPlaying={() => setVideoReady(true)}
        onError={() => { setVideoReady(false); setVideoFailed(true); }}
        aria-label={t('studioVideoAria', { name })}
        className={`pointer-events-none absolute inset-0 h-full w-full rounded-[16px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`} />}
      <span aria-hidden className={`pointer-events-none absolute inset-0 rounded-[16px] ring-inset ring-[#4ABDE9] group-focus-visible:ring-2 ${selected ? 'ring-2' : ''}`} />
    </button>
    {videoFailed && <p role="status" className="mt-1 text-xs text-[#7A4B28]">{t('studioVideoUnavailable')}</p>}
  </article>;
}
